/**
 * iframe 通信相关 Hooks 与工具函数。
 *
 * 包含：消息监听、消息发送、iframe 环境检测。
 * 适用于编辑器以 iframe 形式嵌入父页面的场景。
 */
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store/modalStore";

export interface IframeMessage<T = unknown> {
  type: string;
  payload?: T;
}
/**
 * 检测当前页面是否运行在 iframe 中。
 *
 * 跨域时访问 window.top 会抛出 SecurityError，
 * 同样说明当前处于 iframe 环境，统一返回 true。
 */
export const checkIsInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

/**
 * 读取 Redux 中的 isIframe 标志。
 *
 */
export const useIsIframe = (): boolean => {
  return useSelector((state: RootState) => state.userStore.isIframe);
};

// ─── 接收父页面消息 ───────────────────────────────────────────────────────────

/**
 * 监听父页面通过 postMessage 发来的消息。
 *
 * @param onMessage   收到消息时的回调函数
 * @param allowedOrigin 允许的来源域名，默认 '*' 接受任意来源
 */
export const useIframeMessage = <T = unknown>(
  onMessage: (message: IframeMessage<T>) => void,
  allowedOrigin: string = "*"
): void => {
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // 来源校验（生产环境建议传入具体 origin）
      if (allowedOrigin !== "*" && event.origin !== allowedOrigin) {
        return;
      }
      // 过滤非对象消息（如浏览器扩展、React DevTools 等发出的消息）
      if (!event.data || typeof event.data !== "object") {
        return;
      }
      onMessage(event.data as IframeMessage<T>);
    };

    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, [onMessage, allowedOrigin]);
};

// ─── 发送消息到父页面 ─────────────────────────────────────────────────────────

/**
 * 向父页面发送 postMessage（纯函数，可在类组件中直接调用）。
 *
 * @param type         消息类型
 * @param payload      消息载荷
 * @param targetOrigin 父页面的 origin，默认 '*'
 */
export const postMessageToParent = <T = unknown>(
  type: string,
  payload?: T,
  targetOrigin: string = "*"
): void => {
  if (checkIsInIframe()) {
    window.parent.postMessage({ type, payload } as IframeMessage<T>, targetOrigin);
  }
};

/**
 * 返回一个向父页面发送 postMessage 的函数（Hook，供函数组件使用）。
 *
 * 返回的 send 函数引用稳定（useCallback 包裹），可安全放入依赖数组。
 * 与 postMessageToParent 的区别：isIframe 从 Redux 读取，具有响应性。
 *
 * @param targetOrigin 父页面的 origin，默认 '*'
 * @returns send 函数，调用后向 window.parent 发送消息
 */
export const useSendMessageToParent = (
  targetOrigin: string = "*"
): (<T = unknown>(message: IframeMessage<T>) => void) => {
  const isIframe = useIsIframe();
  return useCallback(
    <T = unknown>(message: IframeMessage<T>) => {
      if (isIframe) {
        window.parent.postMessage(message, targetOrigin);
      }
    },
    [targetOrigin, isIframe]
  );
};
